/**
 * IndexedDB Revision Storage
 *
 * Local-first storage implementation for revision tracking.
 * All revisions are stored in IndexedDB with no network calls for privacy.
 */

import type {
  Revision,
  RevisionHistoryFilter,
  RevisionSettings,
  RevisionStats,
  SyncStatus,
} from '../types';
import {
  REVISION_DB_NAME,
  REVISION_DB_VERSION,
  REVISION_STORES,
} from '../types';

export class IndexedDBRevisionStorage {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  constructor() {
    this.initPromise = this.initialize();
  }

  /**
   * Initialize IndexedDB database
   */
  private async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(REVISION_DB_NAME, REVISION_DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open revision database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create revisions store
        if (!db.objectStoreNames.contains(REVISION_STORES.REVISIONS)) {
          const revisionStore = db.createObjectStore(
            REVISION_STORES.REVISIONS,
            {
              keyPath: 'id',
            }
          );

          // Create indexes for efficient queries
          revisionStore.createIndex('fileId', 'fileId', { unique: false });
          revisionStore.createIndex('filePath', 'filePath', { unique: false });
          revisionStore.createIndex('createdAt', 'createdAt', {
            unique: false,
          });
          revisionStore.createIndex('contentHash', 'contentHash', {
            unique: false,
          });
          revisionStore.createIndex(
            'fileId_revisionNumber',
            ['fileId', 'revisionNumber'],
            {
              unique: true,
            }
          );
          revisionStore.createIndex('syncStatus', 'syncStatus.isSynced', {
            unique: false,
          });
        }

        // Create metadata store
        if (!db.objectStoreNames.contains(REVISION_STORES.METADATA)) {
          const metadataStore = db.createObjectStore(REVISION_STORES.METADATA, {
            keyPath: 'id',
            autoIncrement: true,
          });
          metadataStore.createIndex('type', 'type', { unique: false });
        }

        // Create settings store
        if (!db.objectStoreNames.contains(REVISION_STORES.SETTINGS)) {
          db.createObjectStore(REVISION_STORES.SETTINGS, {
            keyPath: 'id',
          });
        }
      };
    });
  }

  /**
   * Ensure database is initialized
   */
  private async ensureInitialized(): Promise<void> {
    if (this.initPromise) {
      await this.initPromise;
    }
    if (!this.db) {
      throw new Error('Revision database not initialized');
    }
  }

  /**
   * Create a new revision
   */
  async createRevision(revision: Revision): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [REVISION_STORES.REVISIONS],
        'readwrite'
      );
      const store = transaction.objectStore(REVISION_STORES.REVISIONS);

      const request = store.add(revision);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get a revision by ID
   */
  async getRevision(revisionId: string): Promise<Revision | null> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [REVISION_STORES.REVISIONS],
        'readonly'
      );
      const store = transaction.objectStore(REVISION_STORES.REVISIONS);

      const request = store.get(revisionId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get revision history for a file
   */
  async getRevisionHistory(filter: RevisionHistoryFilter): Promise<Revision[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [REVISION_STORES.REVISIONS],
        'readonly'
      );
      const store = transaction.objectStore(REVISION_STORES.REVISIONS);

      let request: IDBRequest;

      // Use appropriate index based on filter
      if (filter.fileId) {
        const index = store.index('fileId');
        request = index.getAll(filter.fileId);
      } else if (filter.filePath) {
        const index = store.index('filePath');
        request = index.getAll(filter.filePath);
      } else {
        request = store.getAll();
      }

      request.onsuccess = () => {
        let results: Revision[] = request.result || [];

        // Apply additional filters
        if (filter.dateRange) {
          results = results.filter(
            (r) =>
              r.createdAt >= filter.dateRange!.from &&
              r.createdAt <= filter.dateRange!.to
          );
        }

        if (filter.syncStatus) {
          results = results.filter((r) => {
            if (!r.syncStatus) return filter.syncStatus === 'pending';
            if (filter.syncStatus === 'synced') return r.syncStatus.isSynced;
            if (filter.syncStatus === 'failed') {
              return !r.syncStatus.isSynced && r.syncStatus.syncError;
            }
            return !r.syncStatus.isSynced;
          });
        }

        if (filter.changeType) {
          results = results.filter(
            (r) => r.metadata?.changeType === filter.changeType
          );
        }

        // Sort by creation date (newest first)
        results.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

        // Apply pagination
        if (filter.offset) {
          results = results.slice(filter.offset);
        }
        if (filter.limit) {
          results = results.slice(0, filter.limit);
        }

        resolve(results);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get latest revision for a file
   */
  async getLatestRevision(fileId: string): Promise<Revision | null> {
    const history = await this.getRevisionHistory({
      fileId,
      limit: 1,
    });
    return history[0] || null;
  }

  /**
   * Update revision sync status
   */
  async updateSyncStatus(
    revisionId: string,
    syncStatus: SyncStatus
  ): Promise<void> {
    await this.ensureInitialized();

    const revision = await this.getRevision(revisionId);
    if (!revision) {
      throw new Error(`Revision ${revisionId} not found`);
    }

    revision.syncStatus = syncStatus;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [REVISION_STORES.REVISIONS],
        'readwrite'
      );
      const store = transaction.objectStore(REVISION_STORES.REVISIONS);

      const request = store.put(revision);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get revisions pending sync
   */
  async getPendingSyncRevisions(): Promise<Revision[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [REVISION_STORES.REVISIONS],
        'readonly'
      );
      const store = transaction.objectStore(REVISION_STORES.REVISIONS);
      const index = store.index('syncStatus');

      const request = index.getAll(); // Get all revisions from syncStatus index

      request.onsuccess = () => {
        const results = request.result || [];
        // Filter to ensure we only get pending ones (not failed)
        const pending = results.filter(
          (r) =>
            !r.syncStatus || (!r.syncStatus.isSynced && !r.syncStatus.syncError)
        );
        resolve(pending);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete a revision
   */
  async deleteRevision(revisionId: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [REVISION_STORES.REVISIONS],
        'readwrite'
      );
      const store = transaction.objectStore(REVISION_STORES.REVISIONS);

      const request = store.delete(revisionId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete all revisions for a file
   */
  async deleteFileRevisions(fileId: string): Promise<void> {
    await this.ensureInitialized();

    const revisions = await this.getRevisionHistory({ fileId });

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [REVISION_STORES.REVISIONS],
        'readwrite'
      );
      const store = transaction.objectStore(REVISION_STORES.REVISIONS);

      let deleteCount = 0;
      const totalDeletes = revisions.length;

      if (totalDeletes === 0) {
        resolve();
        return;
      }

      revisions.forEach((revision) => {
        const request = store.delete(revision.id);

        request.onsuccess = () => {
          deleteCount++;
          if (deleteCount === totalDeletes) {
            resolve();
          }
        };

        request.onerror = () => reject(request.error);
      });
    });
  }

  /**
   * Prune old revisions
   */
  async pruneOldRevisions(fileId: string, keepCount: number): Promise<number> {
    await this.ensureInitialized();

    const revisions = await this.getRevisionHistory({ fileId });

    if (revisions.length <= keepCount) {
      return 0;
    }

    const toDelete = revisions.slice(keepCount);
    let deletedCount = 0;

    for (const revision of toDelete) {
      // Don't delete snapshots unless they're very old
      if (!revision.isSnapshot || revisions.indexOf(revision) > keepCount * 2) {
        await this.deleteRevision(revision.id);
        deletedCount++;
      }
    }

    return deletedCount;
  }

  /**
   * Get revision statistics
   */
  async getStatistics(): Promise<RevisionStats> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [REVISION_STORES.REVISIONS],
        'readonly'
      );
      const store = transaction.objectStore(REVISION_STORES.REVISIONS);

      const request = store.getAll();

      request.onsuccess = () => {
        const revisions = request.result || [];

        const fileIds = new Set<string>();
        let totalStorageBytes = 0;
        let syncedCount = 0;
        let pendingCount = 0;
        let failedCount = 0;

        revisions.forEach((revision) => {
          fileIds.add(revision.fileId);
          totalStorageBytes += revision.size;

          if (revision.syncStatus?.isSynced) {
            syncedCount++;
          } else if (revision.syncStatus?.syncError) {
            failedCount++;
          } else {
            pendingCount++;
          }
        });

        const sortedRevisions = revisions.sort((a, b) =>
          a.createdAt.localeCompare(b.createdAt)
        );

        resolve({
          totalRevisions: revisions.length,
          totalStorageBytes,
          filesWithRevisions: fileIds.size,
          averageRevisionsPerFile:
            fileIds.size > 0 ? revisions.length / fileIds.size : 0,
          oldestRevision: sortedRevisions[0]?.createdAt,
          newestRevision:
            sortedRevisions[sortedRevisions.length - 1]?.createdAt,
          syncStats: {
            syncedCount,
            pendingCount,
            failedCount,
          },
        });
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get revision settings
   */
  async getSettings(): Promise<RevisionSettings> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [REVISION_STORES.SETTINGS],
        'readonly'
      );
      const store = transaction.objectStore(REVISION_STORES.SETTINGS);

      const request = store.get('default');

      request.onsuccess = () => {
        // Return default settings if none exist
        resolve(
          request.result || {
            autoRevision: true,
            maxRevisionsPerFile: 100,
            snapshotInterval: 10,
            enableDeltaCompression: true,
            enableCloudSync: false,
            autoPruneDays: 30,
            revisionDebounceMs: 1000,
          }
        );
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update revision settings
   */
  async updateSettings(settings: Partial<RevisionSettings>): Promise<void> {
    await this.ensureInitialized();

    const currentSettings = await this.getSettings();
    const newSettings = { ...currentSettings, ...settings, id: 'default' };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [REVISION_STORES.SETTINGS],
        'readwrite'
      );
      const store = transaction.objectStore(REVISION_STORES.SETTINGS);

      const request = store.put(newSettings);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all revision data
   */
  async clearAll(): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [REVISION_STORES.REVISIONS, REVISION_STORES.METADATA],
        'readwrite'
      );

      const revisionStore = transaction.objectStore(REVISION_STORES.REVISIONS);
      const metadataStore = transaction.objectStore(REVISION_STORES.METADATA);

      const clearRevisions = revisionStore.clear();
      const clearMetadata = metadataStore.clear();

      let clearedCount = 0;
      const totalClears = 2;

      const checkComplete = () => {
        clearedCount++;
        if (clearedCount === totalClears) {
          resolve();
        }
      };

      clearRevisions.onsuccess = checkComplete;
      clearRevisions.onerror = () => reject(clearRevisions.error);

      clearMetadata.onsuccess = checkComplete;
      clearMetadata.onerror = () => reject(clearMetadata.error);
    });
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.initPromise = null;
    }
  }
}
